import { trackUpgradeClicked } from '@sparklayer-io/spark-dash-core/lib/mixpanel';
import { useHandlers, useSite } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Badge, Button, ExternalLink, Icon } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';

const COMPONENT = 'features:home.components.upsell' satisfies I18nPrefix;

export type UpsellItem = {
  title: string;
  image: string;
  description: string;
  link: string;
  badge?: 'starter' | 'growth' | 'pro';
  /** The current plan doesn't include this feature at all — the CTA upgrades the plan. */
  needsUpgrade?: boolean;
  /** The plan includes it, but it still needs turning on — the CTA opens where to do that. */
  action?: string;
  /** Extra classes on the card, e.g. to hide it below a breakpoint. */
  className?: string;
};

const BADGE_VARIANT = { starter: 'blue', growth: 'purple', pro: 'default' } as const;
const BADGE_PLAN_KEY = { starter: 'v2_basic', growth: 'v2_growth', pro: 'v2_pro' } as const;

function UpsellBadge({ badge }: { badge: NonNullable<UpsellItem['badge']> }) {
  return <Badge variant={BADGE_VARIANT[badge]}>{t(`labels:plans.${BADGE_PLAN_KEY[badge]}`)}</Badge>;
}

function UpsellCard({ item }: { item: UpsellItem }) {
  const site = useSite();
  const { navigationHandler } = useHandlers();
  const { action } = item;

  return (
    <div
      className={cn(
        'flex flex-col overflow-clip rounded-xl border border-gray-200 bg-white shadow-sm',
        item.className,
      )}
    >
      <a href={item.link} target="_blank" rel="noopener noreferrer">
        <img src={item.image} alt="" className="block h-auto w-full border-gray-200 border-b" />
      </a>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <span className="font-medium text-gray-600 text-sm">{item.title}</span>
        {item.badge && <UpsellBadge badge={item.badge} />}
        <p className="text-gray-500 text-sm">
          {item.description} <ExternalLink link={item.link}>{t('links:learn_more')}</ExternalLink>
        </p>
        <div className="mt-auto flex">
          {item.needsUpgrade ? (
            <Button
              variant="secondary"
              icon="upgrade"
              onClick={() => {
                trackUpgradeClicked(site, item.title);
                navigationHandler.navigateById('plan');
              }}
            >
              {t('buttons:upgrade')}
            </Button>
          ) : (
            action && (
              <Button
                variant="secondary"
                icon="settings"
                onClick={() => navigationHandler.navigate(action)}
              >
                {t('buttons:enable')}
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export function Upsell({ items }: { items: UpsellItem[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex sm:mb-6">
        <span className="mr-4 flex size-12 shrink-0 items-center justify-center rounded-lg bg-indigo-50 p-3 align-middle text-spark-purple">
          <Icon name="upgrade" size="lg" />
        </span>
        <div>
          <span className="font-medium text-gray-600 text-sm">{t(`${COMPONENT}.title`)}</span>
          <p className="mt-1 text-gray-500 text-sm">{t(`${COMPONENT}.description`)}</p>
        </div>
      </div>

      <div
        className={cn(
          'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4',
          items.length > 4 && '2xl:grid-cols-5',
        )}
      >
        {items.map((item) => (
          <UpsellCard key={item.title} item={item} />
        ))}
      </div>
    </div>
  );
}
